import React, { FC } from 'react';
import { TerritoryExplorePostsPinnedByAdminSection } from './TerritoryExplorePostsPinnedByAdminSection';
import { useReactiveAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import { TerritoryExploreSection } from './TerritoryExploreSection';
import { ArticlesSortingDropdownKey } from '../TerritoryArticlesSection/constants';
import { TerritoryPromptsContainer } from './TerritoryPromptsContainer';
import { useReactiveHasPermission } from '../Permissions/hooks/useHasPermission';
import { useIsPinEnabled } from '../Territory/hooks/useIsPinEnabled';
import { PromptsSortingDropdownKey } from '../PromptsListSection/constants';
import { TerritoryVersionType } from '../../layouts/TerritoryPageLayout/TerritoryPageLayout';
import getTerritoryPostsPageUrl from '../../services/pagesUrl/getTerritoryPostsPageUrl';
import getTerritoryPromptsPageUrl from '../../services/pagesUrl/getTerritoryPromptsPageUrl';

type TTerritoryExploreAllSectionsProps = {
  territoryId: string;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
};

export const TerritoryExploreAllSections: FC<TTerritoryExploreAllSectionsProps> = ({
  territoryId,
  territorySlug,
  territoryVersion,
}) => {
  const { authenticatedUserId, loading: isAuthLoading, isAuthenticated } = useReactiveAuthenticatedUser();
  const hasPermission = useReactiveHasPermission();
  const { isPinningEnabled } = useIsPinEnabled();
  const canManagePinnedArticles =
    hasPermission('GLOBAL_CAN_MANAGE_PINNED_ARTICLES') ||
    hasPermission('TERRITORY_CAN_MANAGE_PINNED_ARTICLES', territoryId);

  const postsSeeAllUrl = getTerritoryPostsPageUrl(territorySlug);
  const promptsSeeAllUrl = getTerritoryPromptsPageUrl(territorySlug);

  const recommendedPostsSortBy = authenticatedUserId
    ? ArticlesSortingDropdownKey.RecommendedArticles
    : ArticlesSortingDropdownKey.MostTimePoints;

  const recommendedPromptsSortBy = isAuthenticated
    ? PromptsSortingDropdownKey.RecommendedPrompts
    : PromptsSortingDropdownKey.MostReplies;

  return (
    <div className="mb-24">
      <TerritoryExplorePostsPinnedByAdminSection
        territoryId={territoryId}
        isPinningEnabled={isPinningEnabled}
        canManagePinnedArticles={canManagePinnedArticles}
        hideSortingDropdown
        isAuthLoading={isAuthLoading}
        hideSeeAllButton
      />

      <TerritoryExploreSection
        sectionTitle="Recommended Posts"
        territoryId={territoryId}
        authenticatedUserId={authenticatedUserId}
        isPinningEnabled={isPinningEnabled}
        canManagePinnedArticles={canManagePinnedArticles}
        sortBy={recommendedPostsSortBy}
        hideSortingDropdown
        territorySlug={territorySlug}
        territoryVersion={territoryVersion}
        seeAllUrl={postsSeeAllUrl}
      />

      <TerritoryPromptsContainer
        territoryId={territoryId}
        sectionTitle="Recommended Prompts"
        isPinningEnabled={isPinningEnabled}
        canManagePinnedArticles={canManagePinnedArticles}
        sortBy={recommendedPromptsSortBy}
        hideSortingDropdown
        territorySlug={territorySlug}
        territoryVersion={territoryVersion}
        seeAllUrl={promptsSeeAllUrl}
      />

      <TerritoryExploreSection
        sectionTitle="Newest Posts"
        territoryId={territoryId}
        authenticatedUserId={authenticatedUserId}
        isPinningEnabled={isPinningEnabled}
        canManagePinnedArticles={canManagePinnedArticles}
        sortBy={ArticlesSortingDropdownKey.Newest}
        hideSortingDropdown
        isAuthLoading={isAuthLoading}
        territorySlug={territorySlug}
        territoryVersion={territoryVersion}
        seeAllUrl={postsSeeAllUrl}
        isOnExplorePage
      />

      <TerritoryExploreSection
        sectionTitle="Most Time Points"
        territoryId={territoryId}
        authenticatedUserId={authenticatedUserId}
        isPinningEnabled={isPinningEnabled}
        canManagePinnedArticles={canManagePinnedArticles}
        sortBy={ArticlesSortingDropdownKey.MostTimePoints}
        hideSortingDropdown
        territorySlug={territorySlug}
        territoryVersion={territoryVersion}
        seeAllUrl={postsSeeAllUrl}
        isOnExplorePage
      />
    </div>
  );
};
